// src/hooks/useWartungen.js
// --- WARTUNGEN EINES MONTEURS -------------------------------------------------
// Liefert die Daten fuer die Startseite: was steht an, was ist angefangen,
// was wartet auf das Buero.
//
// Bewusst schlanke Spaltenauswahl: die Tabelle protokolle hat eine grosse
// data-Spalte, die hier nichts zu suchen hat. select('*') wuerde sie bei jedem
// Oeffnen der App mitschleppen.

import { useState, useEffect, useCallback } from 'react';
import { supabase, withTimeout } from '../lib/supabase';

const SPALTEN = [
  'id', 'status', 'art', 'wartungs_datum', 'wartungs_uhrzeit',
  'stationsbezeich', 'kunde_name', 'standort',
  'station_id', 'zugewiesen_an', 'zugewiesen_an_id', 'geaendert_am',
  // Planung des Bueros: der Monteur muss VOR der Fahrt wissen, was ansteht.
  'plan_oelprobe', 'plan_evu_dabei', 'plan_umz_schutzpruefung', 'plan_bemerkung',
  // Stationsdaten fuer die Kurzbeschreibung (Bauart, Isolierart, Trafozahl)
  'stationen(bezeichnung, stations_nr, standort_strasse, standort_ort, vnb_stationsbezeichnung,'
    + ' objekt_abweichend, objekt_name, objekt_strasse, objekt_plz, objekt_ort,'
    + ' kunden(name),'
    + ' station_konfiguration(stations_typ, ms_anlage_isolierart),'
    + ' station_trafos(id, trafoart, active))',
].join(',');

const TYP_TEXT = { kompakt: 'Kompaktstation', begehbar: 'begehbar', im_gebaeude: 'im Gebäude', container: 'Container', baustrom: 'Baustromstation', sonstige: '' };
const ISO_TEXT = { luft: 'MS-Anlage Luft (gekapselt)', luft_offen: 'MS-Anlage Luft offen', sf6: 'MS-Anlage SF6-Gas', clean_air: 'MS-Anlage Clean Air' };
const OELPROBE_TEXT = { normal: 'Ölprobe normal', mit_pcb: 'Ölprobe mit PCB' };

// Ortszeit, nicht UTC: toISOString() lieferte zwischen 0 und 2 Uhr (Sommerzeit)
// noch das Datum von gestern.
const heuteIso = () => {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
};

// Sortiert nach Termin; ohne Uhrzeit gilt der Tagesanfang.
function nachTermin(a, b) {
  const d = (a.wartungs_datum || '').localeCompare(b.wartungs_datum || '');
  if (d !== 0) return d;
  return (a.wartungs_uhrzeit || '00:00').localeCompare(b.wartungs_uhrzeit || '00:00');
}

// Nur diese Status kommen auf die Startseite. Vorher wurden ALLE Protokolle
// geladen und erst im Browser aussortiert - mit jedem abgeschlossenen Protokoll
// wurde die Startseite also langsamer.
const START_STATUS = ['vorbereitet', 'zugewiesen', 'entwurf', 'monteur_abgeschlossen'];

// Die zuletzt geladene Liste bleibt auf dem Geraet. Beim naechsten Oeffnen
// steht sie sofort da, nachgeladen wird im Hintergrund. Wer morgens die App
// aufmacht, soll seine Wartungen sehen und nicht "wird geladen ...".
const cacheKey = (userId, istBuero) => 'etb-wartungen-' + (istBuero ? 'buero' : userId);
function ausCache(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}
function inCache(key, daten) {
  try { localStorage.setItem(key, JSON.stringify(daten)); } catch { /* voll oder gesperrt */ }
}

export function useMeineWartungen(userId, { istBuero = false } = {}) {
  const [anstehend, setAnstehend] = useState([]);   // geplant, ab heute
  const [offen, setOffen] = useState([]);           // angefangen, noch nicht abgeschlossen
  const [wartetAufBuero, setWartetAufBuero] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const verteile = useCallback((alle) => {
    const heute = heuteIso();
    setAnstehend(alle
      .filter(p => ['vorbereitet', 'zugewiesen'].includes(p.status)
                && (p.wartungs_datum || '') >= heute)
      .sort(nachTermin));
    // Auch geplante Wartungen, deren Termin vorbei ist: angefangen, aber noch
    // nicht als "angefangen" markiert (offline), oder schlicht verpasst.
    // Vorher verschwanden sie am Tag danach von der Startseite.
    setOffen(alle
      .filter(p => p.status === 'entwurf'
        || (['vorbereitet', 'zugewiesen'].includes(p.status) && (p.wartungs_datum || '') < heute))
      .sort((a, b) => (b.geaendert_am || '').localeCompare(a.geaendert_am || '')));
    setWartetAufBuero(alle
      .filter(p => p.status === 'monteur_abgeschlossen')
      .sort((a, b) => (b.geaendert_am || '').localeCompare(a.geaendert_am || '')));
  }, []);

  const laden = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    const key = cacheKey(userId, istBuero);

    // Zuerst das, was wir schon haben - ohne Wartezeit.
    const gemerkt = ausCache(key);
    if (gemerkt) { verteile(gemerkt); setLoading(false); }
    else setLoading(true);
    setError(null);

    try {
      let q = supabase.from('protokolle').select(SPALTEN).in('status', START_STATUS);
      // Das Buero sieht alles, der Monteur nur seine eigenen. Die RLS-Regeln
      // erzwingen das ohnehin - hier geht es um die Anzeige.
      if (!istBuero) {
        q = q.or('zugewiesen_an_id.eq.' + userId + ',erstellt_von_id.eq.' + userId);
      }
      const { data, error: e } = await withTimeout(
        q.order('wartungs_datum', { ascending: true, nullsFirst: false }).limit(1000),
        20000, 'Wartungen laden'
      );
      if (e) throw e;

      const alle = data || [];
      verteile(alle);
      inCache(key, alle);
    } catch (e) {
      // Mit gemerkter Liste stoert ein Fehler nicht weiter - die Anzeige steht.
      if (!gemerkt) setError(e.message || 'Wartungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [userId, istBuero, verteile]);

  useEffect(() => { laden(); }, [laden]);

  return { anstehend, offen, wartetAufBuero, loading, error, reload: laden };
}

// --- Anzeige-Helfer ----------------------------------------------------------

export function terminText(datum, uhrzeit) {
  if (!datum) return 'ohne Termin';
  const [j, m, t] = datum.split('-');
  const wochentage = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const d = new Date(datum + 'T12:00:00');
  const heute = new Date(); heute.setHours(12, 0, 0, 0);
  const tage = Math.round((d - heute) / 86400000);

  let tagText;
  if (tage === 0) tagText = 'Heute';
  else if (tage === 1) tagText = 'Morgen';
  else if (tage > 1 && tage < 7) tagText = wochentage[d.getDay()];
  else tagText = t + '.' + m + '.' + j;

  // Uhrzeit ist optional. Ohne sie steht einfach nur der Tag da.
  if (!uhrzeit) return tagText;
  return tagText + ', ' + String(uhrzeit).slice(0, 5) + ' Uhr';
}

export function stationText(p) {
  return p.stationen?.bezeichnung || p.stationsbezeich || 'Station ohne Namen';
}

// Wohin faehrt der Monteur? Zuerst die Objektanschrift (wenn sie vom
// Auftraggeber abweicht), sonst der Standort der Station. Der Standort ist beim
// Anlegen mit der Kundenanschrift vorbelegt - der Monteur sah deshalb oft die
// Adresse der Hausverwaltung statt die der Anlage (Anna-Lena, 17.09.2026).
export function fahrAnschrift(s) {
  if (!s) return '';
  if (s.objekt_abweichend && (s.objekt_strasse || s.objekt_ort)) {
    return [s.objekt_strasse, [s.objekt_plz, s.objekt_ort].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
  }
  return [s.standort_strasse, s.standort_ort].filter(Boolean).join(' · ');
}

export function ortText(p) {
  return fahrAnschrift(p.stationen) || p.standort || '';
}

// Kurzbeschreibung der Anlage: "H1073 · begehbar · 1 Trafo · MS-Anlage SF6"
// Was nicht erfasst ist, wird weggelassen statt mit einem Platzhalter gefuellt.
export function anlageText(p) {
  const s = p.stationen;
  if (!s) return '';
  const k = Array.isArray(s.station_konfiguration) ? s.station_konfiguration[0] : s.station_konfiguration;
  const trafos = (s.station_trafos || []).filter(t => t.active !== false);

  const teile = [];
  // Das Stationsformular schreibt den VNB-Namen in stations_nr, der Import
  // in vnb_stationsbezeichnung - beides zaehlt.
  const vnbName = s.vnb_stationsbezeichnung || s.stations_nr;
  if (vnbName && vnbName !== s.bezeichnung) teile.push(vnbName);
  if (k?.stations_typ && TYP_TEXT[k.stations_typ]) teile.push(TYP_TEXT[k.stations_typ]);
  if (trafos.length > 0) teile.push(trafos.length === 1 ? '1 Trafo' : trafos.length + ' Trafos');
  if (k?.ms_anlage_isolierart && ISO_TEXT[k.ms_anlage_isolierart]) teile.push(ISO_TEXT[k.ms_anlage_isolierart]);
  return teile.join(' · ');
}

// Was das Buero fuer diesen Termin vorgesehen hat. Leere Liste = nichts Besonderes.
export function hinweise(p) {
  const h = [];
  if (p.art === 'sichtpruefung') h.push('Sichtprüfung');
  if (p.plan_oelprobe && OELPROBE_TEXT[p.plan_oelprobe]) h.push(OELPROBE_TEXT[p.plan_oelprobe]);
  if (p.plan_evu_dabei) h.push('EVU kommt dazu');
  if (p.plan_umz_schutzpruefung) h.push('UMZ-Schutzprüfung');
  return h;
}

// --- Wartungen in einem Zeitraum (fuer die Kalenderansicht) ------------------
// Anders als useMeineWartungen: hier zaehlt der Termin, nicht der Status.
// Auch abgeschlossene Wartungen sollen im Kalender stehen - sonst waere der
// Rueckblick leer.
const KALENDER_STATUS = [
  'vorbereitet', 'zugewiesen', 'entwurf',
  'monteur_abgeschlossen', 'buero_freigegeben', 'abgeschlossen',
];

export function useWartungenImZeitraum(vonIso, bisIso, { userId, istBuero = false } = {}) {
  const [wartungen, setWartungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const laden = useCallback(async () => {
    if (!vonIso || !bisIso) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      let q = supabase.from('protokolle').select(SPALTEN)
        .in('status', KALENDER_STATUS)
        .gte('wartungs_datum', vonIso)
        .lte('wartungs_datum', bisIso);
      if (!istBuero && userId) {
        q = q.or('zugewiesen_an_id.eq.' + userId + ',erstellt_von_id.eq.' + userId);
      }
      const { data, error: e } = await withTimeout(
        q.order('wartungs_datum', { ascending: true }).limit(300),
        20000, 'Kalender laden'
      );
      if (e) throw e;
      setWartungen((data || []).sort(nachTermin));
    } catch (e) {
      setError(e.message || 'Kalender konnte nicht geladen werden');
      setWartungen([]);
    } finally {
      setLoading(false);
    }
  }, [vonIso, bisIso, userId, istBuero]);

  useEffect(() => { laden(); }, [laden]);

  return { wartungen, loading, error, reload: laden };
}

// Status als kurzer, verstaendlicher Text fuer die Anzeige.
export const STATUS_TEXT = {
  vorbereitet: 'vorbereitet',
  zugewiesen: 'zugewiesen',
  entwurf: 'in Arbeit',
  monteur_abgeschlossen: 'Monteur fertig',
  buero_freigegeben: 'freigegeben',
  abgeschlossen: 'abgeschlossen',
};
